import React from 'react';
import Script from 'next/script';
import { NavMenuPrimitives } from '@abyss/web/ui/NavMenu';
import { PageHeaderPrimitives } from '@abyss/web/ui/PageHeader';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { dispatchEvent } from '@abyss/parcels/tools/dispatchEvent';
import { useRouter } from '@abyss/web/hooks/useRouter';
import { useGetThemePortalProps } from '@/hooks/useGetThemePortalProps';
import {
  LogoBar,
  LogoLink,
  LogoBrandmark,
  LanguageSelectorNavMenuItem,
  MainNavLinkItem,
  LanguageSelectorNavMenuRoot,
  LanguageSelectorItem,
  LanguageSelectorButton,
  HeaderFlexContainer,
  MainNavRoot,
  MainNavItemList,
  NavigationFlexContainer,
} from './styles';
import { NavLinks } from './constants';

import { useLagoon } from '@/hooks/useLagoon';
import { useLanguage } from '@/components/Language/LanguageProvider';
import { LANGUAGES, LanguageObject } from '@/components/Language/types';
import { formatLagoonData } from '../../../utils/formatLagoonData';
import { PSXBlueGreenCanaryParcelUrl } from '../../../utils/utils';
import { getCexRolloutUrl } from '../../../utils/utils';
import { useFlags } from 'launchdarkly-react-client-sdk';

export const CommonHeader = () => {
  const { getLocation, getSearchParams } = useRouter();
  const flags = useFlags();
  const [searchParams] = getSearchParams();
  const psxParcelUrl = PSXBlueGreenCanaryParcelUrl(searchParams);
  const cexParcelUrl = getCexRolloutUrl(flags, searchParams);
  const PSX_PARCEL_URL = `${psxParcelUrl}?ts=${Date.now()}`;
  const CEX_PARCEL_URL = `${cexParcelUrl}?ts=${Date.now()}`;
  const { portalName, portalTheme } = useGetThemePortalProps();
  const { locale, setLocale } = useLanguage();

  const data = useLagoon('container-ui-messaging')() ?? [];
  const navData = formatLagoonData(data, NavLinks);

  const currentPathname = getLocation()?.pathname ?? '/';

  const onFindCareLinkClick = () => {
    const event = new Event('find-care-link-clicked');
    window.dispatchEvent(event);
  };
  const onCostEstimateLinkClick = () => {
    dispatchEvent('cost-estimate-link-clicked');
  };

  const onLanguageSelect = (language: LanguageObject) => {
    setLocale(language);

    if (typeof window === 'undefined') {
      return;
    }

    const currentUrl = new URL(window.location.href);

    if (currentUrl.searchParams.get('locale') === language.locale) {
      return;
    }

    currentUrl.searchParams.set('locale', language.locale);
    window.history.replaceState(null, document.title, currentUrl.toString());
  };

  const displayLanguages = Object.keys(LANGUAGES).map((languageKey, index) => {
    const language = LANGUAGES[languageKey];
    return (
      <LanguageSelectorItem
        title=""
        key={index}
        onClick={() => onLanguageSelect(language)}
        data-testid={`header-language-selector-${language.name}-link`}
        data-auto-testid={`header-language-selector-${language.name}-link`}
        //@ts-expect-error : isStandardAnchor is missing in the type definition in Abyss
        isStandardAnchor={true}
      >
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          {language.title}
          {language === locale ? <IconSymbol icon="check_small" /> : null}
        </div>
      </LanguageSelectorItem>
    );
  });

  return (
    <React.Fragment>
      <PageHeaderPrimitives.Provider>
        <LanguageSelectorNavMenuRoot zIndex={10} enableOutsideScroll={true}>
          <NavMenuPrimitives.List>
            <LanguageSelectorNavMenuItem>
              <LanguageSelectorButton
                variant="inverted"
                data-testid="header-language-selector-dropdown"
                data-auto-testid="header-language-selector-dropdown"
              >
                {locale.title}
              </LanguageSelectorButton>
              <NavMenuPrimitives.Content>
                <NavMenuPrimitives.Columns>
                  <NavMenuPrimitives.Column columnCount={1}>
                    {displayLanguages}
                  </NavMenuPrimitives.Column>
                </NavMenuPrimitives.Columns>
              </NavMenuPrimitives.Content>
            </LanguageSelectorNavMenuItem>
          </NavMenuPrimitives.List>
        </LanguageSelectorNavMenuRoot>
        <LogoBar>
          <HeaderFlexContainer>
            <LogoLink
              data-testid="header-logo-home-link"
              data-auto-testid="header-logo-home-link"
              variant="native"
              href="/findcare"
            >
              <LogoBrandmark
                logo={portalTheme?.logo}
                data-testid={`header-logo-${portalName}-img`}
                data-auto-testid={`header-logo-${portalName}-img`}
              />
            </LogoLink>
          </HeaderFlexContainer>
        </LogoBar>
        <MainNavRoot zIndex={0}>
          <NavigationFlexContainer>
            <MainNavItemList>
              <MainNavLinkItem
                data-testid="header-nav-menu-find-care-link-container"
                data-auto-testid="header-nav-menu-find-care-link-container"
                isActive={!currentPathname?.startsWith('/costestimate')}
              >
                <NavMenuPrimitives.Link
                  data-testid="header-nav-menu-find-care-link"
                  data-auto-testid="header-nav-menu-find-care-link"
                  href="/findcare"
                  onClick={onFindCareLinkClick}
                >
                  {navData[NavLinks.FIND_CARE]?.title ?? 'Find a provider'}
                </NavMenuPrimitives.Link>
              </MainNavLinkItem>
              <MainNavLinkItem
                data-testid="header-nav-menu-price-care-link-container"
                data-auto-testid="header-nav-menu-price-care-link-container"
                isActive={currentPathname?.startsWith('/costestimate')}
              >
                <NavMenuPrimitives.Link
                  data-testid="header-nav-menu-price-care-link"
                  data-auto-testid="header-nav-menu-price-care-link"
                  href="/costestimate"
                  onClick={onCostEstimateLinkClick}
                  //@ts-expect-error : reloadDocument is missing in the type definition in Abyss
                  reloadDocument={true}
                >
                  {navData[NavLinks.PRICE_CARE]?.title ?? 'Estimate costs'}
                </NavMenuPrimitives.Link>
              </MainNavLinkItem>
            </MainNavItemList>
          </NavigationFlexContainer>
        </MainNavRoot>
      </PageHeaderPrimitives.Provider>
      <Script src={PSX_PARCEL_URL} />
      <Script src={CEX_PARCEL_URL} />
    </React.Fragment>
  );
};
